import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ConfirmationModal, useAlert, useModalControl } from '@frontend/design-system';
import { calendarEventsTrackingIds } from './calendar-events-tracking-ids';

interface DeleteAppointmentButtonProps {
  onDelete: () => void | Promise<void>;
}

export const DeleteAppointmentButton = ({ onDelete }: DeleteAppointmentButtonProps) => {
  const { t } = useTranslation('schedule');
  const modalControl = useModalControl();

  return (
    <>
      <Button
        iconName='trash'
        variant='tertiary'
        size='large'
        destructive
        css={deleteButtonStyles}
        {...modalControl.triggerProps}
        trackingId={calendarEventsTrackingIds.deleteAppointmentEventButton}
      >
        {t('Delete')}
      </Button>
      <ConfirmationModal
        destructive
        title={t('Delete Appointment')}
        message={t('This appointment will be removed from the schedule. This action cannot be undone.')}
        confirmLabel={t('Delete')}
        onConfirm={onDelete}
        {...modalControl.modalProps}
      />
    </>
  );
};

interface DeleteAppointmentButtonV3Props {
  calendarEventId: string;
  onSuccess?: () => void;
}

export const DeleteAppointmentButtonV3 = ({ calendarEventId, onSuccess }: DeleteAppointmentButtonV3Props) => {
  const alerts = useAlert();
  const { t } = useTranslation('schedule');
  const deleteCalendarEventsMutation = SchedulerV3.Mutations.useDeleteCalendarEventsMutation();

  const deleteAppointment = async () => {
    try {
      await deleteCalendarEventsMutation.mutateAsync({ eventIds: [calendarEventId] });
      alerts.success(t('Successfully deleted appointment'));
      onSuccess?.();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alerts.error(t('Failed to delete appointment. Please try again.'));
    }
  };

  return <DeleteAppointmentButton onDelete={deleteAppointment} />;
};

const deleteButtonStyles = css({
  position: 'absolute',
  bottom: theme.spacing(4),
});
